/**
 * @file LoadingOverlayProvider.tsx
 * @description
 * ------------------------------------------
 * Context provider para gestionar un overlay de carga global
 * ------------------------------------------
 * @version 1.0.0a
 * @created 2026-02-26
 * @author Correcta
 * @author Daniel Sentamanas
 * @copyright © Correcta
 * @license Private
 * */

"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePathname } from "@/i18n/routing";
import { CircleLoadingState } from "@/components/states/CircleLoadingState";

type LoadingOverlaySource = "navigation" | "data";

type LoadingOverlayActions = {
  show: (_source?: LoadingOverlaySource) => void;
  hide: (_source?: LoadingOverlaySource) => void;
  setDataLoading: (_loading: boolean) => void;
};

type LoadingOverlayState = {
  active: boolean;
  mode: LoadingOverlaySource;
};

const LoadingOverlayActionsContext =
  createContext<LoadingOverlayActions | null>(null);

const LoadingOverlayStateContext = createContext<LoadingOverlayState | null>(
  null,
);

export function LoadingOverlayProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const [navigationActive, setNavigationActive] = useState(false);
  const [dataActive, setDataActive] = useState(false);

  const show = useCallback((source: LoadingOverlaySource = "navigation") => {
    if (source === "data") {
      setDataActive(true);
      return;
    }
    setNavigationActive((prev) => prev || true);
  }, []);

  const hide = useCallback((source: LoadingOverlaySource = "navigation") => {
    if (source === "data") {
      setDataActive(false);
      return;
    }
    setNavigationActive(false);
  }, []);

  const setDataLoading = useCallback((loading: boolean) => {
    setDataActive(loading);
  }, []);

  const actionsValue = useMemo(
    () => ({
      show,
      hide,
      setDataLoading,
    }),
    [hide, setDataLoading, show],
  );

  const stateValue = useMemo((): LoadingOverlayState => {
    const mode: LoadingOverlaySource = navigationActive ? "navigation" : "data";

    return {
      active: navigationActive || dataActive,
      mode,
    };
  }, [dataActive, navigationActive]);

  useEffect(() => {
    if (!pathname) return;
    setNavigationActive(false);
    setDataActive(false);
  }, [pathname]);

  useEffect(() => {
    if (!navigationActive) return;
    const id = setTimeout(() => {
      setNavigationActive(false);
    }, 4000);
    return () => clearTimeout(id);
  }, [navigationActive]);

  return (
    <LoadingOverlayActionsContext.Provider value={actionsValue}>
      <LoadingOverlayStateContext.Provider value={stateValue}>
        {children}
        {stateValue.active && (
          <section
            className={`loader-page-overlay loader-page-overlay--${stateValue.mode}`}
            aria-live="polite"
          >
            <CircleLoadingState />
          </section>
        )}
      </LoadingOverlayStateContext.Provider>
    </LoadingOverlayActionsContext.Provider>
  );
}

export function useLoadingOverlayActions() {
  const ctx = useContext(LoadingOverlayActionsContext);
  if (!ctx)
    throw new Error(
      "useLoadingOverlayActions debe usarse dentro de <LoadingOverlayProvider>",
    );
  return ctx;
}

export function useLoadingOverlayState() {
  const ctx = useContext(LoadingOverlayStateContext);
  if (!ctx)
    throw new Error(
      "useLoadingOverlayState debe usarse dentro de <LoadingOverlayProvider>",
    );
  return ctx;
}

export function useLoadingOverlay() {
  const actions = useLoadingOverlayActions();
  const state = useLoadingOverlayState();
  return useMemo(
    () => ({
      ...actions,
      ...state,
    }),
    [actions, state],
  );
}
